import { InlineChartData } from '@trovata/app/shared/models/inline-chart.model';

// ==========================================
// Enums
// ==========================================

export enum AgentStatus {
	draft = 'draft',
	active = 'active',
	paused = 'paused',
	error = 'error',
}

export enum AgentTriggerType {
	scheduled = 'scheduled',
	threshold = 'threshold',
	monitoring = 'monitoring',
}

export enum Cadence {
	hourly = 'hourly',
	daily = 'daily',
	weekly = 'weekly',
}

/** Type guard narrowing a wire-boundary string to the `Cadence` enum. */
export function isCadence(value: string | null | undefined): value is Cadence {
	return value === Cadence.hourly || value === Cadence.daily || value === Cadence.weekly;
}

export enum ExecutionStatus {
	success = 'success',
	noAction = 'no_action',
	error = 'error',
	pending = 'pending',
	warning = 'warning',
}

// Values are uppercase by convention. The API returns lowercase roles — normalized via .toUpperCase() in LoadAgentWithHistory.
// This is an intentional exception to the project convention of lowercase enum values.
export enum ChatRole {
	user = 'USER',
	assistant = 'ASSISTANT',
}

export enum AgentsListTab {
	activity = 'activity',
	listing = 'listing',
}

export enum ActivityStatus {
	warning = 'warning',
	alert = 'alert',
	noAction = 'no_action',
	success = 'success',
	pending = 'pending',
}

// ==========================================
// Activity Models
// ==========================================

export interface ActivityEntry {
	readonly id: string;
	readonly agentId: string;
	readonly agentName: string;
	readonly title: string;
	readonly status: ActivityStatus;
	readonly timestamp: string;
	readonly content: string;
}

export interface ActivityDateGroup {
	readonly date: string;
	readonly entries: readonly ActivityEntry[];
}

// ==========================================
// Agent Models
// ==========================================

export interface Agent {
	readonly identifier: string;
	readonly sessionId: string;
	readonly name: string;
	readonly prompt: string;
}

// ==========================================
// Chat Models
// ==========================================

export interface ChatMessage {
	readonly role: ChatRole;
	readonly content: string;
}

// ==========================================
// API Response Models
// ==========================================

export interface CreateAgentResponse {
	readonly agentId: string;
	readonly sessionId: string;
	readonly name: string;
	readonly prompt: string;
	// Optional at the wire boundary: defended with `?? []` in mapping so a response that
	// omits it (e.g. an older API instance mid-rollout) can't break the UI.
	readonly emails?: readonly string[];
}

export interface AgentsResponse {
	readonly agents: AgentListItem[];
}

export interface AgentListItem {
	readonly agentId: string;
	readonly sessionId: string;
	readonly userId: string;
	readonly name: string;
	readonly prompt: string;
	readonly emails?: readonly string[];
	readonly paused: boolean;
	readonly status: string;
	readonly rule: NotificationRuleItem | null;
	readonly createdAt: string | null;
	readonly updatedAt: string | null;
	readonly lastRunTimestamp: string | null;
}

export interface AgentDetailResponse {
	readonly agent: AgentDetailItem;
}

export interface AgentDetailItem {
	readonly agentId: string;
	readonly sessionId: string;
	readonly userId: string;
	readonly name: string;
	readonly prompt: string;
	readonly emails?: readonly string[];
	readonly instantiated: boolean;
	readonly paused: boolean;
	readonly status: string;
	readonly rule: NotificationRuleItem | null;
	readonly messages: readonly MessageItem[];
	readonly invocations: readonly AgentInvocationItem[];
	readonly createdAt: string | null;
	readonly updatedAt: string | null;
	readonly lastRunTimestamp: string | null;
}

export interface MessageItem {
	readonly role: string;
	readonly content: string;
}

export interface AgentInvocationItem {
	readonly invocationId: string;
	readonly agentId: string;
	readonly title: string;
	readonly response: string;
	readonly status: string;
	readonly createdAt: string | null;
}

export interface InvokeAgentResponse {
	readonly invocationId: string;
	readonly agentId: string;
	readonly status: string;
}

export interface PatchAgentBody {
	readonly agentId: string;
	readonly name?: string;
	readonly prompt?: string;
	readonly paused?: boolean;
	readonly emails?: readonly string[];
	readonly ruleConfig?: RuleConfigItem | null;
}

// ==========================================
// Activity Feed Response Models
// ==========================================

export interface InvocationAgentItem {
	readonly agentId: string;
	readonly name: string;
	readonly prompt: string;
}

export interface AgentInvocationDetailItem {
	readonly invocationId: string;
	readonly title: string;
	readonly response: string;
	readonly status: string;
	readonly createdAt: string | null;
	readonly agent: InvocationAgentItem;
}

export interface AgentInvocationsResponse {
	readonly invocations: readonly AgentInvocationDetailItem[];
}

/**
 * Full detail for a single invocation from `GET /agents/invocations/{invocationId}`.
 *
 * Unlike the list items above, this carries `inlineCharts`: a map keyed by the
 * `{{CHART:<token>}}` tokens embedded in `response`, null for runs recorded
 * before charts existed. The list route stays lean and never includes charts.
 */
export interface InvocationDetailItem {
	readonly invocationId: string;
	readonly title: string;
	readonly response: string;
	readonly status: string;
	readonly createdAt: string | null;
	readonly endedAt: string | null;
	readonly duration: number | null;
	readonly agent: InvocationAgentItem;
	readonly inlineCharts: Readonly<Record<string, InlineChartData>> | null;
}

export interface InvocationDetailResponse {
	readonly invocation: InvocationDetailItem;
}

// ==========================================
// Notification Rule Models
// ==========================================

export interface NotificationRuleItem {
	readonly ruleId: string | null;
	readonly ruleType: string | null;
	readonly ruleConfig: RuleConfigItem | null;
	readonly lastEvaluatedAt: string | null;
}

export interface RuleConfigItem {
	readonly name: string | null;
	readonly cron: string | null;
	readonly timezone: string | null;
	readonly details: RuleConfigDetailsItem | null;
	readonly deliveryTypes: readonly string[] | null;
}

export interface RuleConfigDetailsItem {
	readonly cadence: string | null;
	readonly cadenceDetails: RuleConfigCadenceDetailsItem | null;
	readonly accountIds: readonly string[] | null;
	readonly conditions: (IngestionTriggerConditionItem | BalanceTriggerConditionItem)[] | null;
}

export interface RuleConfigCadenceDetailsItem {
	readonly times: CadenceTime[] | null;
}

export interface CadenceTime {
	readonly daysOfWeek: number[] | null;
	readonly hour: number | null;
	readonly minute: number;
}

// ==========================================
// Schedule Trigger Details (UI Layer)
// ==========================================

/** A single time entry in a daily or weekly schedule (12-hour format for UI display). */
export interface AgentTimeEntry {
	readonly id: number;
	readonly hour: number;
	readonly minute: number;
	readonly ampm: 'am' | 'pm';
}

export interface HourlyTriggerDetails {
	readonly cadence: Cadence.hourly;
	readonly minute: number;
	readonly timezone: string;
}

export interface DailyTriggerDetails {
	readonly cadence: Cadence.daily;
	readonly times: readonly AgentTimeEntry[];
	readonly timezone: string;
}

export interface WeeklyTriggerDetails {
	readonly cadence: Cadence.weekly;
	readonly days: readonly string[];
	readonly times: readonly AgentTimeEntry[];
	readonly timezone: string;
}

export type ScheduleTriggerDetails = HourlyTriggerDetails | DailyTriggerDetails | WeeklyTriggerDetails;

export interface IngestionTriggerConditionItem {
	readonly dataTypes: readonly string[];
	readonly condition: string;
}

export interface BalanceTriggerConditionItem {
	readonly balanceKey: string | null;
	readonly operator: string | null;
	readonly value: number | null;
}

// ==========================================
// Extended Agent (UI Layer)
// ==========================================

/**
 * Canonical agent entity stored in the normalized state map.
 *
 * Extends the base `Agent` wire type with UI-layer fields (`status`, `type`,
 * `triggerConfig`, `executionHistory`, etc.) populated from the API response
 * during mapping. Consumers treat this as the authoritative view of an agent
 * for rendering the listing, detail panel, and builder dialog.
 */
export interface AgentDetail extends Agent {
	readonly status: AgentStatus;
	readonly type: AgentTriggerType;
	readonly description: string;
	/** Email distribution list persisted on the agent (top-level `emails` API field). */
	readonly emails: readonly string[];
	readonly lastRun: string | null;
	readonly nextRun: string | null;
	readonly lastModified: string | null;
	readonly createdOn: string | null;
	readonly triggers: number;
	readonly triggerConfig: TriggerConfig;
	readonly executionHistory: readonly ExecutionHistoryEntry[];
}

// ==========================================
// Configuration Models
// ==========================================

/**
 * Trigger configuration for an agent — describes when and how the agent runs.
 *
 * All fields are optional because this shape doubles as the builder form's
 * working state. The config form is populated incrementally as the user
 * selects a trigger type, then a cadence, then schedule details; at any
 * intermediate point, some fields are legitimately unset.
 *
 * Consumer-contract invariants (enforced at save time via `canActivate`,
 * not at the type level):
 * - When `type === 'scheduled'`, callers must ensure `cadence` is set.
 * - When `cadence` is set, `scheduleDetails` must match the cadence variant
 *   (`HourlyTriggerDetails` for `Cadence.hourly`, etc.).
 *
 * A stricter discriminated union is tracked in the feature-wide deferred
 * list; migrating requires introducing an intermediate all-optional form
 * shape alongside a strict saved shape.
 */
export interface TriggerConfig {
	readonly type?: AgentTriggerType;
	readonly cadence?: Cadence;
	readonly scheduleDetails?: ScheduleTriggerDetails;
}

// ==========================================
// Execution History
// ==========================================

export interface ExecutionHistoryEntry {
	readonly timestamp: string;
	readonly status: ExecutionStatus;
	readonly description: string;
}

// ==========================================
// Config Form State
// ==========================================

/**
 * Delivery configuration for the builder form.
 *
 * `recipients` is the raw, comma-separated email input as typed. It is parsed
 * into the agent's `emails` string array (see `parseRecipients`) when saving
 * via PATCH, and rehydrated from `AgentDetail.emails` (see `formatRecipients`)
 * when an existing agent is loaded.
 */
export interface AgentDeliveryConfig {
	readonly recipients: string;
}

/**
 * Builder config-form state, tracked independently from saved agent data.
 *
 * Enables `hasUnsavedChanges` and `canActivate` selectors without touching
 * the normalized `AgentDetail` entity. Mirrors a subset of agent fields
 * (`name`, `instructions`, `triggerConfig`) plus form-specific flags
 * (`isDirty`, `isValid`) that don't belong on the saved entity.
 */
export interface AgentFormState {
	readonly name: string;
	readonly instructions: string;
	readonly triggerConfig: TriggerConfig;
	readonly delivery: AgentDeliveryConfig;
	readonly isDirty: boolean;
	readonly isValid: boolean;
}

// ==========================================
// Chat UI Models
// ==========================================

export interface ParsedChatMessage extends ChatMessage {
	readonly finprompt?: string;
	readonly textContent: string;
	/** Inline charts delivered on the terminal frame of the stream that produced this message, keyed by `{{CHART:<token>}}` token. */
	readonly inlineCharts?: Readonly<Record<string, InlineChartData>> | null;
}

/** Greeting variant determines which greeting layout the chat component renders. */
export type GreetingVariant = 'new-chat' | 'new-agent' | 'continue-agent' | 'edit-agent';

// ==========================================
// Builder Dialog / Panel Types
// ==========================================

/** Mode in which the agent builder dialog is opened. `null` when the dialog is closed. */
export type BuilderMode = 'new' | 'continue' | 'edit';

/** Active tab within the agent builder dialog. */
export type BuilderActiveTab = 'configuration' | 'preview';

/** Save-menu action emitted by agent-save-menu / agent-builder-top-bar. */
export type BuilderSaveAction = 'save-draft' | 'save-activate' | 'save-pause' | 'save-copy-draft';

/** Overflow-menu (builder context) action emitted by agent-context-menu / agent-builder-top-bar. */
export type BuilderMenuAction = 'close' | 'duplicate' | 'delete';

/** Listing-context action emitted by agent-context-menu / agent-list-item / agent-detail-panel. */
export type AgentListingAction = 'view' | 'edit' | 'duplicate' | 'execute' | 'pause' | 'resume' | 'delete';

/** Context-menu action event carrying both the selected action and its target agent. */
export interface AgentContextActionEvent {
	readonly action: AgentListingAction;
	readonly agent: AgentDetail;
}

/** Type of detail panel shown on the right of the agents page. */
export type AgentsPanelType = 'agent' | 'activity';

export interface SuggestionCard {
	readonly text: string;
	readonly icon: string;
}

/**
 * Starter-template card that pre-populates the config form with a full agent
 * config when clicked.
 *
 * Distinct from `SuggestionCard`, which sends plain text as a chat message:
 * starters carry a `defaultPrompt` and `defaultTriggerConfig` used to seed
 * the builder form, bypassing the chat-based configuration flow.
 */
export interface StarterTemplateCard {
	readonly name: string;
	readonly description: string;
	readonly defaultPrompt: string;
	readonly defaultTriggerConfig: TriggerConfig;
}
